import React, { useEffect, useState } from "react";
import { client } from "../../../api/client";
import { fetchLnpPage } from "../../../api/lnp";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import type { ILnp } from "../../../type";

interface ImportJob {
  id: number;
  target_model: string;
  filename: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  status: string;
  created_at: string;
  created_by?: string;
}

interface Props {
  inCartPks: Set<number>;
  onAddMany: (rows: ILnp[]) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  active: boolean;
  onRefreshDashboard?: () => void;
}

const MigrationBatchBrowserPanel: React.FC<Props> = ({
  inCartPks,
  onAddMany,
  showInfo,
  showConfirm,
  active,
  onRefreshDashboard,
}) => {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  const loadJobs = async () => {
    setLoading(true);
    try {
      // Use is_admin parameter if supported or just get all completed
      const resp = await client.get("/search/admin/import/?status=completed");
      setJobs(resp.data.results || resp.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) {
      loadJobs();
    }
  }, [active]);

  const handleAddSelected = async () => {
    if (selectedJobIds.size === 0) return;
    setIsBulkLoading(true);
    try {
      let collectedRows: ILnp[] = [];
      // For each job, fetch all rows
      for (const jobId of selectedJobIds) {
        let p = 1;
        while (true) {
          const resp = await client.get(`/search/admin/lnp/?filter={"import_job_id":${jobId}}&page=${p}&page_size=200`);
          const data = resp.data.results || resp.data;
          if (data.length === 0) break;
          collectedRows = collectedRows.concat(data);
          if (data.length < 200) break;
          p++;
        }
      }
      
      const toAdd = collectedRows.filter((r) => !inCartPks.has(r.index));
      if (toAdd.length > 0) {
        onAddMany(toAdd);
        showInfo({
          title: "Added",
          message: `Added ${toAdd.length} rows to cart.`,
          variant: "success",
        });
      } else {
        showInfo({
          title: "Already in cart",
          message: "All selected rows are already in the cart.",
          variant: "info",
        });
      }
      setSelectedJobIds(new Set());
    } catch (error) {
      console.error(error);
      showInfo({
        title: "Error",
        message: "Failed to fetch rows for selected batches.",
        variant: "warning",
      });
    } finally {
      setIsBulkLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedJobIds.size === 0) return;
    showConfirm({
      title: "Delete Selected Batches",
      message: `Are you sure you want to delete ${selectedJobIds.size} batch(es)? This will remove all their draft rows.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        setIsBulkLoading(true);
        try {
          for (const jobId of selectedJobIds) {
            await client.delete(`/search/admin/import/${jobId}/`);
          }
          showInfo({ title: "Deleted", message: `Deleted ${selectedJobIds.size} batch(es).`, variant: "success" });
          setSelectedJobIds(new Set());
          loadJobs();
          if (onRefreshDashboard) onRefreshDashboard();
        } catch (err) {
          console.error(err);
          showInfo({ title: "Error", message: "Failed to delete batches.", variant: "warning" });
        } finally {
          setIsBulkLoading(false);
        }
      },
    });
  };

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex items-center px-4 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
        <h4 className="text-sm font-medium text-gray-700">Upload Batches</h4>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={loadJobs}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            disabled={selectedJobIds.size === 0 || isBulkLoading}
            onClick={handleAddSelected}
            className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isBulkLoading
              ? "Working..."
              : `Add selected (${selectedJobIds.size})`}
          </button>
          <button
            type="button"
            disabled={selectedJobIds.size === 0 || isBulkLoading}
            onClick={handleDeleteSelected}
            className="px-3 py-1.5 text-xs text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
          >
            Delete selected
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <LoadingSpinner label="Loading batches" />
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 w-10">
                  <input
                    type="checkbox"
                    checked={jobs.length > 0 && selectedJobIds.size === jobs.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedJobIds(new Set(jobs.map((j) => j.id)));
                      } else {
                        setSelectedJobIds(new Set());
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-2 py-1.5">Batch ID</th>
                <th className="px-2 py-1.5">Filename</th>
                <th className="px-2 py-1.5">Rows</th>
                <th className="px-2 py-1.5">Date</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center text-gray-400">
                    No completed upload batches found.
                  </td>
                </tr>
              )}
              {jobs.map((job) => (
                <tr
                  key={job.id}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedJobIds.has(job.id)}
                      onChange={(e) => {
                        const newSet = new Set(selectedJobIds);
                        if (e.target.checked) newSet.add(job.id);
                        else newSet.delete(job.id);
                        setSelectedJobIds(newSet);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-1 text-gray-900 tabular-nums">
                    {job.id}
                  </td>
                  <td className="px-2 py-1 text-gray-700 truncate max-w-[200px]" title={job.filename}>
                    {job.filename}
                  </td>
                  <td className="px-2 py-1 text-gray-700">
                    {job.valid_rows}
                  </td>
                  <td className="px-2 py-1 text-gray-500">
                    {new Date(job.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MigrationBatchBrowserPanel;
